import { useState, useEffect } from 'react';
import {
  createSubscription,
  startTrial,
  selectCurrentPlan
} from '../../../app/store/slices/subscriptionSlice';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../../../assets/images/logos/576_Cash Canvas-02.png'
import { toast } from 'react-toastify';
import { selectCanvases, fetchUserCanvases } from '../../../app/store/slices/canvasSlice';
import { 
  selectUserLoading,
  selectTrialUsed, 
  fetchUserData
} from '../../../app/store/slices/userSlice';
import {billingOptions, allPlans} from '../../../utils/constants/subscriptionConstants';


const UserSubscription = () => {
  const isTestingMode = process.env.REACT_APP_TESTING === 'true';
  const plans = isTestingMode
    ? allPlans
    : allPlans.filter(plan => !plan.testOnly);

  const [selectedPlan, setSelectedPlan] = useState('free');  
  const [billingCycle, setBillingCycle] = useState('Monthly');
  const [isTrialLoading, setIsTrialLoading] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isDataError, setIsDataError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userCanvases = useSelector(selectCanvases);
  const userLoading = useSelector(selectUserLoading);
  const trialUsed = useSelector(selectTrialUsed);
  const currentPlan = useSelector(selectCurrentPlan)?.owned;

  const billingOptionsWithDaily = isTestingMode
    ? ['daily', ...billingOptions]
    : billingOptions; 
  useEffect(() => {
    if (isTestingMode && selectedPlan === 'test') {
      setBillingCycle('daily'); 
    }
  }, [isTestingMode, selectedPlan]);
  const gridColsClass = plans.length === 5
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5'
    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';

  useEffect(() => {
    const loadInitialData = async () => {
      setIsDataLoading(true);
      try {
        await Promise.all([
          dispatch(fetchUserData()).unwrap(),
          dispatch(fetchUserCanvases()).unwrap()
        ]);
      } catch (error) {
        console.error('Failed to load initial data:', error);
        setIsDataError(error.message || 'Failed to load subscription data');
      } finally {
        setIsDataLoading(false);
      }
    };

    loadInitialData();
  }, [dispatch]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleFormSubmit = async (e, isSkip = false) => {
    e.preventDefault();

    // Check if data is still loading
    if (isDataLoading || userLoading) {
      toast.info('Please wait while we load your subscription data...');
      return;
    }

    if (isSkip) {
      setIsTrialLoading(true);
      try {
        if (trialUsed) {
          toast.error('You have already used your trial period');
          return;
        }
        await dispatch(startTrial()).unwrap();
        
        if (!userCanvases ||  userCanvases.owned.length === 0) {
          navigate('/create-canvas');
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        toast.error(err || 'Failed to start trial');
        navigate('/dashboard');
      } finally {
        setIsTrialLoading(false);
      }
      return;
    }

    setIsSubscriptionLoading(true);
    
    try {
      const isTestPlan = selectedPlan === 'test';
      const subscriptionData = {
        planKey: selectedPlan,
        billingCycle: isTestPlan ? 'daily' : billingCycle.toLowerCase()
      };

      // Add validation checks
      if (!selectedPlan || !billingCycle) {
        throw new Error('Please select a plan and billing cycle');
      }

      const shouldCreateSubscription = 
        !currentPlan || selectedPlan === 'free' ||
        (currentPlan?.plan?.planKey === 'trial' && selectedPlan !== 'free') ||
        (currentPlan?.plan?.planKey === 'free' && selectedPlan !== 'trial');

      if (shouldCreateSubscription) {
        const result = await dispatch(createSubscription(subscriptionData)).unwrap();  
        if (!result || !result.data) {
          throw new Error('Failed to create subscription: Invalid response');
        }

        if (result.data?.requiresPayment) {
          navigate('/payment', {
            state: {
              subscriptionId: result.data.subscriptionId,
              planDetails: result.data.plan,
              selectedPlanKey: result.data.plan.planKey,
              billingCycle: result.data.billingCycle,
              clientSecret: result.data.clientSecret
            },
            replace: true
          });
          return;
        }

        if (!userCanvases ||  userCanvases.owned.length === 0) {
          toast.success('Subscription created successfully!');
          navigate('/create-canvas');
        } else {
          toast.success('Subscription created successfully!');
          navigate('/dashboard');
        }

        // // Only navigate to dashboard after successful subscription creation
        // toast.success('Subscription created successfully!');
        // navigate('/dashboard');
      }
    } catch (err) {
      console.error('Subscription error:', err);
      toast.error(err.message || 'Failed to create subscription');
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

 
  if (isDataLoading || userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isDataError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-4 max-w-md">
          <p className="text-red-600 mb-4">{isDataError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry Loading
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        {/* Logo container with smaller responsive sizing */}
    <div className="fixed top-0 left-0 p-4">
      <img 
        src={logo} 
        alt="Cash Canvas Logo" 
        className="h-20 w-auto object-contain max-w-[150px]"
      />
    </div>
      <div className="card-setup">
        <h2 className="txt-page-heading">
          Choose your Subscription or Continue with free trial for 30 days
        </h2>
        <p className="p-tag-below-heading">
          Free trial for 30 days gives you access to all the Cash Canvas features.
          After the 30 day trial you will be reverted to Free with ads
        </p>

        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {billingOptionsWithDaily.map(option => (
            <button
              key={option}
              className={`px-4 py-1 text-sm border rounded-lg transition whitespace-nowrap ${
                billingCycle === option ? 'bg-btn-primary text-white' : 'bg-gray-100'
              }`}
              onClick={() => setBillingCycle(option)}
              disabled={isTestingMode && selectedPlan === 'test' && option !== 'Daily'}
            >
              {option}
            </button>
          ))}
        </div>

        <div className={`grid ${gridColsClass} gap-4 md:gap-6 mb-8`}>
          {plans.map(plan => (
            <div
              key={plan.key}
              onClick={() => handlePlanSelect(plan.key)} 
              className={`p-4 border rounded-xl cursor-pointer transition flex flex-col justify-between h-full ${
                selectedPlan === plan.key
                  ? 'border-btn-primary shadow-md' 
                  : 'border-gray-200 hover:border-gray-400'
              }`}
            >
              <div>
                <h3 className="text-base md:text-lg font-semibold text-center">{plan.title}</h3>
                <p className="text-center font-bold text-lg mb-2">
                  {plan.price[billingCycle.toLowerCase()]}
                </p>
                {billingCycle !== 'Monthly' && plan.key !== 'free' && (
                  <p className="text-center text-green-600 text-sm mb-4">
                    {plan.saveUpTo[billingCycle.toLowerCase()]}
                  </p>
                )}
                <ul className="text-sm space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="text-gray-700 flex items-center">
                      <span className="h-1.5 w-1.5 rounded-full bg-gray-400 mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row justify-between gap-4">
          <button 
            className="btn-back"
            onClick={() => navigate(-1)} // Go back in history
          >
            Back
          </button>

          <button 
            className={`btn-skip ${trialUsed ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={(e) => handleFormSubmit(e, true)}
            disabled={isTrialLoading || isSubscriptionLoading || userLoading || trialUsed}
            title={trialUsed ? 'Trial period already used' : ''}
          >
            {isTrialLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Starting trial...
              </span>
            ) : trialUsed ? (
              'Trial Unavailable! Already Availed Subscription'
            ) : (
              'Skip and Continue with just free trial for 30 days'
            )}
          </button>

          <button 
            className={`btn-continue ${isSubscriptionLoading ? 'opacity-75 cursor-not-allowed' : ''}`}
            onClick={(e) => handleFormSubmit(e, false)}
            disabled={isTrialLoading || isSubscriptionLoading}
          >
            {isSubscriptionLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};


export default UserSubscription;